import { Component, Input, Output, EventEmitter } from '@angular/core';
import { SENTIMENT_ANALYSIS_CONFIG } from '../../sentiment-analysis.config';

export interface HeatmapCellPayload {
  rowLabel: string;
  columnLabel: string;
  /** Normalized cohort key (e.g. over50, promoter) for filtering feedback. */
  columnCohortKey?: string;
  count: number;
  percentage?: number;
  /** Total responses in cell (for "X out of Y total" in feedback panel). */
  total?: number;
}

/** Cell data: label (display), cohortKey (normalized for keying), count, percentage, total. */
export interface HeatmapCellData {
  label: string;
  cohortKey: string;
  count: number;
  percentage?: number;
  total?: number;
}

/** Table row: rowLabel (e.g. theme), optional row total, then cohort cells with % and count. */
export interface HeatmapTableRow {
  rowLabel: string;
  /** Sum of counts across cohort columns (shown before % / count breakdown). */
  rowTotal: number;
  cells: HeatmapCellData[];
  maxCountInRow: number;
}

/**
 * Heatmap table aligned with older Category Heatmap Analysis: row key from themeId/subThemeId,
 * columns from computeHeatmapColumnsFromData (age: 30-50, over50, under30; gender: top by volume; NPS: promoter, passive, detractor).
 * Color: gray if total < minResponses; else (Volume Weight x 60%) + (Sentiment Impact x 40%) normalized by min/max, rgba with dark text when norm >= 0.55.
 */
@Component({
  selector: 'app-sentiment-details-heatmap-widget',
  standalone: false,
  templateUrl: './details-heatmap-widget.component.html',
  styleUrls: ['./details-heatmap-widget.component.scss'],
})
export class SentimentDetailsHeatmapWidgetComponent {
  @Input() heatmapData: any[] = [];
  @Input() cohortType = '';
  /** When set, replaces config defaults for the cohort dimension dropdown (Category Heatmap / header). */
  @Input() cohortOptionList: Array<{ value: string; label: string }> | null = null;
  @Input() level: 'theme' | 'subTheme' = 'theme';
  @Input() sentimentType: 'negative' | 'non_negative' = 'negative';
  @Input() themeId: string | null = null;
  /** When set, the matching row / cohort cell (or row + Total for "All cohorts") shows a selection marker. */
  @Input() selectedCell: HeatmapCellPayload | null = null;
  @Input() loading = false;
  @Input() hideHeader = false;
  @Output() cohortTypeChange = new EventEmitter<string>();
  @Output() levelChange = new EventEmitter<'theme' | 'subTheme'>();
  @Output() sentimentTypeChange = new EventEmitter<'negative' | 'non_negative'>();
  @Output() themeIdChange = new EventEmitter<string | null>();
  @Output() loadHeatmap = new EventEmitter<void>();
  @Output() cellClick = new EventEmitter<HeatmapCellPayload>();
  /** Row label / total clicked — same feedback panel as cell, without cohort filter; uses raw-comments API in parent. */
  @Output() rowClick = new EventEmitter<HeatmapCellPayload>();

  readonly levelOptions = SENTIMENT_ANALYSIS_CONFIG.heatmapLevels;

  /** Built-in cohort types use fixed column ordering / NPS normalization; anything else is treated as a dynamic tag field. */
  private static readonly builtinCohortTypes = new Set([
    'ageGroup',
    'gender',
    'npsCategory',
    'rating_bucket',
    'interactionType',
  ]);

  get resolvedCohortOptions(): Array<{ value: string; label: string }> {
    return this.cohortOptionList?.length ? [...this.cohortOptionList] : [];
  }

  private isBuiltinCohortType(): boolean {
    return SentimentDetailsHeatmapWidgetComponent.builtinCohortTypes.has(this.cohortType);
  }
  readonly sentimentOptions: { value: 'negative' | 'non_negative'; label: string }[] = [
    { value: 'negative', label: 'Negative' },
    { value: 'non_negative', label: 'Non-negative' },
  ];

  /** Min responses for cell to be coloured (theme 30, sub-theme 10). */
  get minResponses(): number {
    return this.level === 'subTheme'
      ? (SENTIMENT_ANALYSIS_CONFIG as any).heatmapMinResponsesSubTheme ?? 10
      : (SENTIMENT_ANALYSIS_CONFIG as any).heatmapMinResponsesTheme ?? 30;
  }

  /** First column header: "Themes / Cohort" or "Sub-themes / Cohort". */
  get rowHeaderLabel(): string {
    return this.level === 'subTheme' ? 'Sub-themes / Cohort' : 'Themes / Cohort';
  }

  private toNum(v: unknown): number {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  /** Normalize cohort value for keying (lowercase, NPS: promoter/passive/detractor). */
  normalizeCohortKey(value: string | null | undefined): string {
    const raw = String(value ?? 'unknown').trim().toLowerCase();
    /** Empty / missing cohort must bucket to `unknown` so rows are not dropped and a column can show. */
    if (!raw) return 'unknown';
    if (this.cohortType === 'npsCategory' || this.cohortType === 'rating_bucket') {
      if (raw.startsWith('promot')) return 'promoter';
      if (raw.startsWith('passiv')) return 'passive';
      if (raw.startsWith('detract')) return 'detractor';
    }
    if (!this.isBuiltinCohortType()) {
      return raw.replace(/\s+/g, '').toLowerCase();
    }
    return raw.replace(/\s+/g, '');
  }

  /** Raw cohort label from API row (no case/format changes — used for heatmap column headers). */
  private extractCohortRawDisplay(item: any): string {
    const colRaw =
      item?.cohortValue ??
      item?.cohort ??
      item?.npsCategory ??
      item?.ageGroup ??
      item?.gender ??
      item?.cohort_value ??
      item?.nps_category ??
      item?.age_group ??
      item?.y ??
      '';
    return String(colRaw ?? '').trim();
  }

  private getCanonicalSubThemeHeatmapId(themeId: any, subThemeName: any, subThemeId: any): string {
    const tId = String(themeId ?? '').trim();
    const name = String(subThemeName ?? '').trim();
    const rawId = String(subThemeId ?? '').trim();
    if (tId && name) return `${tId} - ${name}`;
    if (tId && rawId) return `${tId} - ${rawId}`;
    return rawId || name || tId || 'unknown';
  }

  private getHeatmapRowKey(item: any): string {
    if (this.level === 'subTheme') {
      if (item?.subThemeId) return String(item.subThemeId);
      return this.getCanonicalSubThemeHeatmapId(item?.themeId, item?.subThemeName, item?.subThemeId);
    }
    return String(
      item?.themeId ??
        item?.themeName ??
        item?.theme_name ??
        item?.dimensionValue ??
        ''
    );
  }

  private getRowDisplayLabel(item: any): string {
    if (this.level === 'subTheme') return String(item?.subThemeName ?? item?.dimensionValue ?? '').trim() || this.getHeatmapRowKey(item);
    return String(item?.themeName ?? item?.theme_name ?? item?.dimensionValue ?? item?.dimension_value ?? '').trim() || this.getHeatmapRowKey(item);
  }

  /** Column order from data: age 30-50, over50, under30, unknown; gender top by volume; NPS promoter, passive, detractor. */
  private computeHeatmapColumnsFromData(list: any[]): string[] {
    const raw = list || [];
    const cohortField = (d: any) =>
      d?.cohortValue ??
      d?.cohort ??
      d?.npsCategory ??
      d?.ageGroup ??
      d?.gender ??
      d?.cohort_value ??
      d?.age_group ??
      d?.y ??
      'unknown';
    if (this.cohortType === 'ageGroup') {
      const order = ['30-50', 'over50', 'under30', 'unknown'];
      const present = new Set(raw.map((d: any) => this.normalizeCohortKey(cohortField(d))));
      const cols = order.filter((c) => present.has(c));
      return cols.length > 0 ? cols : ['30-50', 'over50', 'under30', 'unknown'];
    }
    const totals = new Map<string, number>();
    for (const d of raw) {
      const c = this.normalizeCohortKey(cohortField(d));
      totals.set(c, (totals.get(c) || 0) + this.toNum(d?.total ?? d?.sentimentCount ?? d?.count ?? 0));
    }
    const entries = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
    /** Hide "other" from gender top-N; keep "unknown" so a column appears when data is unclassified. */
    const excludedForGender = ['other'];
    const top = entries
      .filter((e) => {
        if (this.cohortType === 'gender') return !excludedForGender.includes(e[0].toLowerCase());
        return e[0] !== 'unknown';
      })
      .slice(0, 5)
      .map((e) => e[0]);
    if (this.cohortType === 'gender') {
      const base = top.length > 0 ? top : ['male', 'female'];
      if (totals.has('unknown') && !base.includes('unknown')) {
        return [...base, 'unknown'];
      }
      return base;
    }
    if (this.cohortType === 'npsCategory' || this.cohortType === 'rating_bucket') {
      const order =
        this.cohortType === 'rating_bucket'
          ? ['promoter', 'passive', 'detractor', 'top_2_box', 'middle_bucket', 'bottom_2_box']
          : ['promoter', 'passive', 'detractor'];
      const present = new Set(entries.map((e) => e[0].toLowerCase()));
      const cols = order.filter((c) => present.has(c));
      const out = cols.length > 0 ? cols : [...order];
      if (present.has('unknown')) {
        return out.includes('unknown') ? out : [...out, 'unknown'];
      }
      return out;
    }
    if (!this.isBuiltinCohortType()) {
      const topDyn = entries
        .filter((e) => e[0] !== 'unknown')
        .slice(0, 8)
        .map((e) => e[0]);
      const base = topDyn.length > 0 ? topDyn : entries.map((e) => e[0]).slice(0, 8);
      if (entries.some((e) => e[0] === 'unknown') && !base.includes('unknown')) {
        return [...base, 'unknown'];
      }
      return base.length > 0 ? base : ['unknown'];
    }
    return top.length > 0 ? top : ['unknown'];
  }

  /** Normalize API item to row key, display label, cohort key, count (sentimentCount), percentage (sentiment %), total. */
  private normalizeItem(item: any): { rowKey: string; rowLabel: string; colKey: string; count: number; percentage?: number; total: number } | null {
    const rowKey = this.getHeatmapRowKey(item);
    const rowLabel = this.getRowDisplayLabel(item);
    const colRaw = this.extractCohortRawDisplay(item);
    const colKey = this.normalizeCohortKey(colRaw);
    const count = Math.max(0, this.toNum(item.sentimentCount ?? item.count ?? item.response_count ?? item.value ?? 0));
    const total = Math.max(0, this.toNum(item.total ?? item.totalResponses ?? item.total_count ?? 0));
    const pct = item.sentimentPercentage ?? item.percentage ?? item.percent ?? item.sentiment_percentage ?? item.pct;
    const pctNum = typeof pct === 'number' ? pct : (pct != null ? Number(pct) : undefined);
    const percentage = pctNum !== undefined && Number.isFinite(pctNum) ? pctNum : undefined;
    if (!rowKey || colKey === '') return null;
    return { rowKey, rowLabel, colKey, count, percentage, total: total || count };
  }

  /** Intensity cache keyed by rowKey_colKey, and min/max for normalization. */
  private _intensityByKey = new Map<string, number>();
  private _intensityMin = 0;
  private _intensityMax = 100;

  /** Build table rows and columns; also updates intensity cache for cell styling. */
  get tableRows(): HeatmapTableRow[] {
    const raw = this.heatmapData || [];
    if (!raw.length) {
      this._intensityByKey.clear();
      this._intensityMin = 0;
      this._intensityMax = 100;
      return [];
    }
    const rowMap = new Map<string, string>(); // rowKey -> rowLabel (display)
    const colHeaderByKey = new Map<string, string>();
    const cellMap = new Map<string, Map<string, { count: number; percentage?: number; total: number }>>(); // rowKey -> colKey -> cell
    raw.forEach((item: any) => {
      const norm = this.normalizeItem(item);
      if (!norm) return;
      const { rowKey, rowLabel, colKey, count, percentage, total } = norm;
      const rawCol = this.extractCohortRawDisplay(item);
      if (!colHeaderByKey.has(colKey)) {
        colHeaderByKey.set(colKey, rawCol || colKey);
      }
      rowMap.set(rowKey, rowLabel);
      if (!cellMap.has(rowKey)) cellMap.set(rowKey, new Map());
      const existing = cellMap.get(rowKey)!.get(colKey);
      const tot = total || (existing?.total ?? 0);
      const cnt = count;
      const pct = percentage ?? existing?.percentage;
      cellMap.get(rowKey)!.set(colKey, { count: cnt, percentage: pct, total: tot || cnt });
    });
    const rowKeys = Array.from(rowMap.keys()).sort();
    /** Prefer canonical cohort order, then append any keys present in data (e.g. sentiment `y` when API is not cohort-shaped). */
    const baseCols = this.computeHeatmapColumnsFromData(raw);
    const keysFromData = new Set<string>();
    for (const item of raw) {
      const n = this.normalizeItem(item);
      if (n) keysFromData.add(n.colKey);
    }
    const cols: string[] = [...baseCols];
    for (const k of Array.from(keysFromData).sort()) {
      if (!cols.includes(k)) cols.push(k);
    }
    const minResponses = this.minResponses;
    const maxCount = Math.max(
      ...Array.from(cellMap.values()).flatMap((m) => Array.from(m.values()).map((c) => c.count)),
      1
    );
    this._intensityByKey.clear();
    let minI = Number.POSITIVE_INFINITY;
    let maxI = Number.NEGATIVE_INFINITY;
    for (const [rowKey, colMap] of cellMap) {
      const rowLabel = rowMap.get(rowKey) ?? rowKey;
      for (const [colKey, cell] of colMap) {
        const key = `${rowLabel}_${colKey}`;
        const total = cell.total ?? cell.count;
        const volumeWeight = (cell.count / maxCount) * 100;
        const sentimentImpact = cell.percentage != null && Number.isFinite(cell.percentage) ? cell.percentage : 0;
        const intensity = volumeWeight * 0.6 + sentimentImpact * 0.4;
        this._intensityByKey.set(key, intensity);
        if (total >= minResponses) {
          minI = Math.min(minI, intensity);
          maxI = Math.max(maxI, intensity);
        }
      }
    }
    if (minI !== Number.POSITIVE_INFINITY && maxI !== Number.NEGATIVE_INFINITY && Math.abs(maxI - minI) >= 1e-6) {
      this._intensityMin = minI;
      this._intensityMax = maxI;
    } else {
      this._intensityMin = 0;
      this._intensityMax = 100;
    }
    const result: HeatmapTableRow[] = rowKeys.map((rowKey) => {
      const rowLabel = rowMap.get(rowKey) ?? rowKey;
      const colMap = cellMap.get(rowKey);
      const cells: HeatmapCellData[] = cols.map((cohortKey) => {
        const c = colMap?.get(cohortKey) ?? { count: 0, percentage: undefined, total: 0 };
        return {
          label: colHeaderByKey.get(cohortKey) ?? cohortKey,
          cohortKey,
          count: c.count,
          percentage: c.percentage,
          total: c.total ?? c.count,
        };
      });
      const rowTotal = cells.reduce((sum, c) => sum + c.count, 0);
      /** When API omits sentiment %, show share of row so each cell can show "% (count)". */
      const cellsWithShare: HeatmapCellData[] = cells.map((cell) => {
        if (cell.percentage != null && Number.isFinite(cell.percentage)) {
          return cell;
        }
        if (rowTotal > 0 && cell.count >= 0) {
          return {
            ...cell,
            percentage: (cell.count / rowTotal) * 100,
          };
        }
        return cell;
      });
      const maxCountInRow = Math.max(...cellsWithShare.map((c) => c.count), 1);
      return { rowLabel, rowTotal, cells: cellsWithShare, maxCountInRow };
    });
    return result;
  }

  get hasData(): boolean {
    return (this.heatmapData?.length ?? 0) > 0;
  }

  /** Cell background: gray if total < minResponses; else normalized intensity (Volume*0.6 + Sentiment*0.4) as rgba. */
  getCellColor(rowLabel: string, cell: HeatmapCellData): string {
    const key = `${rowLabel}_${cell.cohortKey}`;
    const total = cell.total ?? cell.count;
    if (total < this.minResponses) return '#d1d5db';
    const intensity = this._intensityByKey.get(key) ?? 0;
    const denom = Math.max(this._intensityMax - this._intensityMin, 1e-6);
    const norm = Math.max(0, Math.min(1, (intensity - this._intensityMin) / denom));
    const alpha = 0.15 + 0.7 * norm;
    const rgb = this.sentimentType === 'negative' ? { r: 239, g: 68, b: 68 } : { r: 34, g: 197, b: 94 };
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  }

  /** True when cell should use dark (white) text for contrast. */
  isCellDark(rowLabel: string, cell: HeatmapCellData): boolean {
    const key = `${rowLabel}_${cell.cohortKey}`;
    const total = cell.total ?? cell.count;
    if (total < this.minResponses) return false;
    const intensity = this._intensityByKey.get(key) ?? 0;
    const denom = Math.max(this._intensityMax - this._intensityMin, 1e-6);
    const norm = Math.max(0, Math.min(1, (intensity - this._intensityMin) / denom));
    return norm >= 0.55;
  }

  /** Row label + Total cells when feedback is for the whole row (not a single cohort cell). */
  isRowScopeSelected(rowLabel: string): boolean {
    const s = this.selectedCell;
    if (!s || String(s.columnLabel ?? '').trim() !== 'All cohorts') return false;
    return String(s.rowLabel ?? '').trim() === String(rowLabel ?? '').trim();
  }

  isCohortCellSelected(rowLabel: string, cell: HeatmapCellData): boolean {
    const s = this.selectedCell;
    if (!s || String(s.columnLabel ?? '').trim() === 'All cohorts') return false;
    if (String(s.rowLabel ?? '').trim() !== String(rowLabel ?? '').trim()) return false;
    const key = String(s.columnCohortKey ?? '').trim();
    if (key && cell.cohortKey === key) return true;
    return String(s.columnLabel ?? '').trim() === String(cell.label ?? '').trim();
  }

  /** Cohort column header highlight when a single cohort cell is selected. */
  isCohortColumnSelected(col: HeatmapCellData): boolean {
    const s = this.selectedCell;
    if (!s || String(s.columnLabel ?? '').trim() === 'All cohorts') return false;
    const key = String(s.columnCohortKey ?? '').trim();
    if (key) return col.cohortKey === key;
    return String(s.columnLabel ?? '').trim() === String(col.label ?? '').trim();
  }

  formatCellText(cell: HeatmapCellData): string {
    const count = Number.isFinite(cell.count) ? Math.round(cell.count) : 0;
    if (cell.percentage != null && Number.isFinite(cell.percentage)) {
      return `${Number(cell.percentage).toFixed(1)}% (${count})`;
    }
    return `${count}`;
  }

  /** Total column: responses in this row across cohorts. */
  formatRowTotal(n: number): string {
    return Number.isFinite(n) ? String(Math.round(n)) : '0';
  }

  onCellClick(rowLabel: string, cell: HeatmapCellData): void {
    this.cellClick.emit({
      rowLabel,
      columnLabel: cell.label,
      columnCohortKey: cell.cohortKey,
      count: cell.count,
      percentage: cell.percentage,
      total: cell.total,
    });
  }

  onRowClick(row: HeatmapTableRow, event: MouseEvent): void {
    event.stopPropagation();
    this.rowClick.emit({
      rowLabel: row.rowLabel,
      columnLabel: 'All cohorts',
      columnCohortKey: undefined,
      count: row.rowTotal,
      total: row.rowTotal,
    });
  }

  onCohortChange(value: string): void {
    this.cohortTypeChange.emit(value);
    this.loadHeatmap.emit();
  }

  onLevelChange(value: string): void {
    this.levelChange.emit(value as 'theme' | 'subTheme');
    this.loadHeatmap.emit();
  }

  onSentimentChange(value: string): void {
    this.sentimentTypeChange.emit(value as 'negative' | 'non_negative');
    this.loadHeatmap.emit();
  }

  onThemeChange(value: string): void {
    this.themeIdChange.emit(value || null);
    this.loadHeatmap.emit();
  }
}
